import React from 'react';
import { useIntl } from 'react-intl';

import { PerformanceInsight, PerformanceOrderTypes } from 'apis/merchant-gateway/insights';

import getInsightTotalsByOrderType, { CalculatedPerformanceTotals } from './getInsightTotalsByOrderType';
import { InsightResponseWithPreviousInterval, OrderType, RowType } from '../../types';
import { formatGrowthRate, getGrowthRateForFields, getGrowthTrend } from '../../utils';
import SummaryTable from '../InsightSummary/SummaryTable';

type OrderTypeTotalsInsightsProps = {
  performanceInsight: InsightResponseWithPreviousInterval<PerformanceInsight>;
  isVATEnabled: boolean;
};

type TotalsTableProps = {
  title: string;
  tooltip: string;
  selectedTotals: CalculatedPerformanceTotals;
  previousTotals?: CalculatedPerformanceTotals;
  currency: Currency;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
};

type OrderTypeTotalsTableProps = {
  selected?: PerformanceInsight;
  previous?: PerformanceInsight;
  orderType: PerformanceOrderTypes;
  title: string;
  tooltip: string;
  isFetching: boolean;
  isError: boolean;
  isVATEnabled: boolean;
  onRetry: () => void;
};

const TotalsTable = ({
  title,
  tooltip,
  selectedTotals,
  previousTotals,
  currency,
  isFetching,
  isError,
  onRetry,
}: TotalsTableProps) => {
  const { formatMessage, formatNumber, locale } = useIntl();

  const growthRate =
    previousTotals &&
    getGrowthRateForFields(selectedTotals, previousTotals, ['sales', 'orders', 'avgBasketValue', 'avgBasketItems']);

  return (
    <SummaryTable
      isFetching={isFetching}
      isError={isError}
      onRetry={onRetry}
      title={formatMessage({ id: title })}
      tooltip={formatMessage({ id: tooltip })}
      rows={[
        {
          type: RowType.Metric,
          title: 'insights.performance.total-sales',
          titleTooltip: 'insights.total-sales.tooltip',
          metrics: formatNumber(selectedTotals.sales, {
            currency,
            style: 'currency',
          }),
          growthRate: formatGrowthRate(growthRate?.sales, locale),
          growthTrend: getGrowthTrend(growthRate?.sales),
        },
        {
          type: RowType.Metric,
          title: 'insights.performance.total-orders',
          titleTooltip: 'insights.total-orders.tooltip',
          metrics: selectedTotals.orders,
          growthRate: formatGrowthRate(growthRate?.orders, locale),
          growthTrend: getGrowthTrend(growthRate?.orders),
        },
        {
          type: RowType.Metric,
          title: 'insights.performance.average-basket-size',
          titleTooltip: 'insights.average-basket-size.tooltip',
          metrics: formatNumber(selectedTotals.avgBasketValue, {
            currency,
            style: 'currency',
          }),
          growthRate: formatGrowthRate(growthRate?.avgBasketValue, locale),
          growthTrend: getGrowthTrend(growthRate?.avgBasketValue),
        },
        {
          type: RowType.Metric,
          title: 'insights.performance.average-basket-size-items',
          titleTooltip: 'insights.average-basket-size-items.tooltip',
          metrics: formatNumber(selectedTotals.avgBasketItems, {
            maximumFractionDigits: 2,
          }),
          growthRate: formatGrowthRate(growthRate?.avgBasketItems, locale),
          growthTrend: getGrowthTrend(growthRate?.avgBasketItems),
        },
      ]}
    />
  );
};

export const OrderTypeTotalsTable = ({
  selected,
  previous,
  orderType,
  title,
  tooltip,
  isFetching,
  isError,
  isVATEnabled,
  onRetry,
}: OrderTypeTotalsTableProps) => {
  const { formatMessage } = useIntl();

  if (!selected || isError) {
    return (
      <SummaryTable
        isFetching={isFetching}
        isError={isError}
        onRetry={onRetry}
        title={formatMessage({ id: title })}
        tooltip={formatMessage({ id: tooltip })}
        rows={[]}
      />
    );
  }

  const selectedTotals = getInsightTotalsByOrderType(selected, isVATEnabled);
  const previousTotals = previous && getInsightTotalsByOrderType(previous, isVATEnabled);

  return (
    <TotalsTable
      isFetching={isFetching}
      isError={isError}
      onRetry={onRetry}
      title={title}
      tooltip={tooltip}
      selectedTotals={selectedTotals[orderType]}
      previousTotals={previousTotals?.[orderType]}
      currency={selected.currency}
    />
  );
};
export const OrderTypeTotalsInsights = ({ performanceInsight, isVATEnabled }: OrderTypeTotalsInsightsProps) => {
  const {
    selected: { data: selected },
    previous: { data: previous },
  } = performanceInsight;

  return (
    <>
      <OrderTypeTotalsTable
        isFetching={performanceInsight.isFetching}
        isError={performanceInsight.isError}
        onRetry={performanceInsight.refetch}
        key={OrderType.Regular}
        selected={selected}
        previous={previous}
        orderType={OrderType.Regular}
        title={'insights.performance.regular-purchases'}
        tooltip={'insights.regular-orders.tooltip'}
        isVATEnabled={isVATEnabled}
      />
      <OrderTypeTotalsTable
        isFetching={performanceInsight.isFetching}
        isError={performanceInsight.isError}
        onRetry={performanceInsight.refetch}
        key={OrderType.WoltPlus}
        selected={selected}
        previous={previous}
        orderType={OrderType.WoltPlus}
        title={'insights.performance.woltplus-purchases'}
        tooltip={'insights.woltplus-sales.tooltip'}
        isVATEnabled={isVATEnabled}
      />
    </>
  );
};
